import { useState } from "react";
import { styles } from "../styles";
import { hasCapability } from "../utils/capabilities";
import BackButton from "../components/BackButton";

export default function WarrantiesPage({
  customer,
  location,
  equipment,
  actions,
  role,
  capabilities,
  onBack,
}) {
  // Source from live customer prop; never the click-time snapshot, so
  // warranties added/edited here show fresh on back-navigate.
  const currentLocation =
    customer?.locations?.find((l) => l.id === location?.id) || null;
  const currentEquipment =
    currentLocation?.equipment?.find((e) => e.id === equipment?.id) || null;

  const canEdit = hasCapability(role, capabilities, "editEquipment");

  const warranties = currentEquipment?.warranties || [];

  const [showAddForm, setShowAddForm] = useState(false);
  const [newType, setNewType] = useState("");
  const [newInstallDate, setNewInstallDate] = useState("");
  const [newExpDate, setNewExpDate] = useState("");
  // Length stays UI-only — it just drives the exp-date auto-fill. We don't
  // persist a separate field because installDate + expDate already capture
  // the contract and the length is derivable from them.
  const [newLength, setNewLength] = useState("");
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [editType, setEditType] = useState("");
  const [editInstallDate, setEditInstallDate] = useState("");
  const [editExpDate, setEditExpDate] = useState("");
  const [editLength, setEditLength] = useState("");

  // Common warranty lengths for the dropdown. "Custom" (blank value) means
  // no auto-fill — admin enters the exp date by hand for unusual terms
  // (90 days, 6 months, lifetime tracked manually, etc.).
  const YEAR_OPTIONS = [1, 2, 3, 5, 10, 20];

  // installDate + N years → YYYY-MM-DD. Returns "" when either side is
  // missing or unparseable so the input stays blank rather than NaN.
  const computeExpDate = (installDate, years) => {
    if (!installDate || !years) return "";
    const [y, m, d] = installDate.split("-").map((s) => parseInt(s, 10));
    if (!y || !m || !d) return "";
    const dt = new Date(y + years, m - 1, d);
    const yy = dt.getFullYear();
    const mm = String(dt.getMonth() + 1).padStart(2, "0");
    const dd = String(dt.getDate()).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString();
  };

  const formatTimestamp = (iso) => {
    if (!iso) return "";
    return new Date(iso).toLocaleString();
  };

  const resetAddForm = () => {
    setNewType("");
    setNewInstallDate("");
    setNewExpDate("");
    setNewLength("");
    setShowAddForm(false);
    setErrorMessage("");
  };

  const handleAdd = async () => {
    if (!newType.trim()) {
      setErrorMessage("Type of warranty is required.");
      return;
    }
    try {
      setSaving(true);
      setErrorMessage("");
      await actions.addEquipmentWarranty(currentEquipment.id, {
        type: newType.trim(),
        installDate: newInstallDate,
        expDate: newExpDate,
      });
      resetAddForm();
    } catch (e) {
      setErrorMessage(e.message);
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (w) => {
    setEditingId(w.id);
    setEditType(w.type || "");
    setEditInstallDate(w.installDate || "");
    setEditExpDate(w.expDate || "");
    setEditLength("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditType("");
    setEditInstallDate("");
    setEditExpDate("");
    setEditLength("");
  };

  const handleSaveEdit = async (id) => {
    if (!editType.trim()) {
      setErrorMessage("Type of warranty is required.");
      return;
    }
    try {
      setSaving(true);
      setErrorMessage("");
      await actions.updateEquipmentWarranty(currentEquipment.id, id, {
        type: editType.trim(),
        installDate: editInstallDate,
        expDate: editExpDate,
      });
      cancelEdit();
    } catch (e) {
      setErrorMessage(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this warranty?")) return;
    try {
      setSaving(true);
      setErrorMessage("");
      await actions.deleteEquipmentWarranty(currentEquipment.id, id);
    } catch (e) {
      setErrorMessage(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!currentEquipment) {
    return (
      <>
        <BackButton onClick={onBack} label="Back to Equipment" />
        <p style={{ fontSize: "13px", color: "#666", marginTop: "8px" }}>
          Loading equipment…
        </p>
      </>
    );
  }

  return (
    <>
      <BackButton onClick={onBack} label="Back to Equipment" />

      <h2>Warranties — {currentEquipment?.type}</h2>

      {errorMessage && (
        <div style={{ color: "red", marginBottom: 12 }}>
          Error: {errorMessage}
        </div>
      )}

      {canEdit && !showAddForm && (
        <button
          style={{ ...styles.primaryButton, marginBottom: "12px" }}
          onClick={() => setShowAddForm(true)}
        >
          + Add Warranty
        </button>
      )}

      {canEdit && showAddForm && (
        <div style={{ ...styles.card, maxWidth: "780px" }}>
          <h3 style={{ marginTop: 0 }}>Add Warranty</h3>
          <div style={{ marginBottom: "8px" }}>
            <label>Type of Warranty: </label>
            <input
              style={styles.input}
              type="text"
              placeholder="e.g. Compressor, Parts, Labor"
              value={newType}
              onChange={(e) => setNewType(e.target.value)}
            />
          </div>
          <div style={{ marginBottom: "8px" }}>
            <label>Startup / Install Date: </label>
            <input
              style={{ ...styles.input, maxWidth: "200px" }}
              type="date"
              value={newInstallDate}
              onChange={(e) => {
                const val = e.target.value;
                setNewInstallDate(val);
                const yrs = parseInt(newLength, 10);
                if (yrs > 0 && val) setNewExpDate(computeExpDate(val, yrs));
              }}
            />
          </div>
          <div style={{ marginBottom: "8px" }}>
            <label>Length: </label>
            <select
              style={{ ...styles.input, maxWidth: "200px" }}
              value={newLength}
              onChange={(e) => {
                const val = e.target.value;
                setNewLength(val);
                const yrs = parseInt(val, 10);
                if (yrs > 0 && newInstallDate) {
                  setNewExpDate(computeExpDate(newInstallDate, yrs));
                }
              }}
            >
              <option value="">Custom (set exp date manually)</option>
              {YEAR_OPTIONS.map((y) => (
                <option key={y} value={y}>
                  {y} year{y > 1 ? "s" : ""}
                </option>
              ))}
            </select>
          </div>
          <div style={{ marginBottom: "8px" }}>
            <label>Warranty Exp Date: </label>
            <input
              style={{ ...styles.input, maxWidth: "200px" }}
              type="date"
              value={newExpDate}
              onChange={(e) => setNewExpDate(e.target.value)}
            />
            <span style={{ fontSize: "11px", color: "#666", marginLeft: "8px" }}>
              Auto-filled from Length; edit to override.
            </span>
          </div>
          <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
            <button
              style={{ ...styles.primaryButton, margin: 0 }}
              onClick={handleAdd}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
            <button
              style={{ ...styles.button, margin: 0 }}
              onClick={resetAddForm}
              disabled={saving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {warranties.length === 0 ? (
        <p style={{ color: "#666", fontStyle: "italic" }}>
          No warranties on record for this unit.
        </p>
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {warranties.map((w) => (
            <li
              key={w.id}
              style={{ ...styles.card, maxWidth: "780px" }}
            >
              {editingId === w.id ? (
                <>
                  <div style={{ marginBottom: "8px" }}>
                    <label>Type of Warranty: </label>
                    <input
                      style={styles.input}
                      type="text"
                      value={editType}
                      onChange={(e) => setEditType(e.target.value)}
                    />
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    <label>Startup / Install Date: </label>
                    <input
                      style={{ ...styles.input, maxWidth: "200px" }}
                      type="date"
                      value={editInstallDate}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditInstallDate(val);
                        const yrs = parseInt(editLength, 10);
                        if (yrs > 0 && val) {
                          setEditExpDate(computeExpDate(val, yrs));
                        }
                      }}
                    />
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    <label>Length: </label>
                    <select
                      style={{ ...styles.input, maxWidth: "200px" }}
                      value={editLength}
                      onChange={(e) => {
                        const val = e.target.value;
                        setEditLength(val);
                        const yrs = parseInt(val, 10);
                        if (yrs > 0 && editInstallDate) {
                          setEditExpDate(
                            computeExpDate(editInstallDate, yrs)
                          );
                        }
                      }}
                    >
                      <option value="">Custom (set exp date manually)</option>
                      {YEAR_OPTIONS.map((y) => (
                        <option key={y} value={y}>
                          {y} year{y > 1 ? "s" : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    <label>Warranty Exp Date: </label>
                    <input
                      style={{ ...styles.input, maxWidth: "200px" }}
                      type="date"
                      value={editExpDate}
                      onChange={(e) => setEditExpDate(e.target.value)}
                    />
                    <span
                      style={{ fontSize: "11px", color: "#666", marginLeft: "8px" }}
                    >
                      Auto-filled from Length; edit to override.
                    </span>
                  </div>
                  <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
                    <button
                      style={{ ...styles.primaryButton, margin: 0 }}
                      onClick={() => handleSaveEdit(w.id)}
                      disabled={saving}
                    >
                      {saving ? "Saving..." : "Save"}
                    </button>
                    <button
                      style={{ ...styles.button, margin: 0 }}
                      onClick={cancelEdit}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                      gap: "6px 16px",
                    }}
                  >
                    <div>
                      <strong>Type:</strong> {w.type || "-"}
                    </div>
                    <div>
                      <strong>Install Date:</strong> {formatDate(w.installDate)}
                    </div>
                    <div>
                      <strong>Exp Date:</strong> {formatDate(w.expDate)}
                    </div>
                  </div>
                  <div
                    style={{
                      fontSize: "12px",
                      color: "#666",
                      marginTop: "8px",
                    }}
                  >
                    Added by {w.createdByName || w.createdBy || "—"} on{" "}
                    {formatTimestamp(w.createdAt)}
                    {w.updatedAt && (
                      <>
                        {" · "}
                        last edited by {w.updatedByName || w.updatedBy || "—"} on{" "}
                        {formatTimestamp(w.updatedAt)}
                      </>
                    )}
                  </div>
                  {canEdit && (
                    <div
                      style={{ display: "flex", gap: "8px", marginTop: "8px" }}
                    >
                      <button
                        style={{ ...styles.button, margin: 0 }}
                        onClick={() => startEdit(w)}
                      >
                        Edit
                      </button>
                      <button
                        style={{
                          ...styles.button,
                          margin: 0,
                          background: "#fee",
                          color: "#a00",
                        }}
                        onClick={() => handleDelete(w.id)}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </>
  );
}
